/**
 * Main Slash Commands Hook
 * 
 * ChatInput'a entegre edilecek ana hook
 * Tüm slash command logic'ini birleştirir
 * Zero prop drilling pattern
 */

import { useCallback } from 'react';
import { useSlashCommandStore } from '../store/slash-command-store';
import { useCommandParser } from './use-command-parser';
import { useCommandExecutor } from './use-command-executor';
import type { SlashCommand } from '../types/command-types';

export const useSlashCommands = (customSetMessage?: (message: string) => void) => {
  const { 
    isMenuOpen, 
    openMenu, 
    closeMenu, 
    selectedIndex,
    setSelectedIndex,
    resetSelectedIndex,
  } = useSlashCommandStore();
  
  const { parseInput } = useCommandParser();
  const { executeCommand } = useCommandExecutor(customSetMessage);
  
  // NOT: Suggestions'lar CommandSuggestionMenu component'inde yönetiliyor
  // Bu hook'ta suggestions çağırmıyoruz çünkü CommandSuggestionMenu zaten kendi suggestions'ını yönetiyor
  // Bu sayede duplicate request'ler önleniyor
  
  /**
   * Handle Input Change
   * 
   * Input değiştiğinde menu'yü aç/kapat
   */
  const handleInputChange = useCallback((value: string) => {
    const { isCommand } = parseInput(value);
    
    // "/" ile başladıysa ve menu kapalıysa aç
    if (isCommand && !isMenuOpen) {
      openMenu();
    }
    
    // "/" yoksa ve menu açıksa kapat
    if (!isCommand && isMenuOpen) {
      closeMenu();
    }
    
    // Input değiştiğinde selection'ı sıfırla
    if (isCommand) {
      resetSelectedIndex();
    }
  }, [isMenuOpen, openMenu, closeMenu, parseInput, resetSelectedIndex]);
  
  /**
   * Handle Command Select
   * 
   * Command seçildiğinde çalıştır
   */
  const handleCommandSelect = useCallback(async (command: SlashCommand) => {
    const result = await executeCommand(command);
    
    // Navigation ve action command'lar için menu kapatıldı
    // Parent ve dynamic command'lar için menu açık kalıyor
    
    return result;
  }, [executeCommand]);
  
  /**
   * Handle Selected Command Execute
   * 
   * Keyboard ile seçili command'ı çalıştır
   * NOT: suggestions parametre olarak alınmalı (CommandSuggestionMenu'den)
   */
  const handleSelectedCommandExecute = useCallback(async (suggestions: SlashCommand[]) => {
    if (suggestions.length > 0 && selectedIndex < suggestions.length) {
      const selectedCommand = suggestions[selectedIndex];
      await handleCommandSelect(selectedCommand);
    }
  }, [selectedIndex, handleCommandSelect]);
  
  /**
   * Handle Keyboard Navigation
   * 
   * Arrow keys ile suggestion'lar arasında gezinme
   * Enter ile seçim
   * Escape ile kapama
   * NOT: suggestions parametre olarak alınmalı (CommandSuggestionMenu'den)
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent, suggestions: SlashCommand[]) => {
    if (!isMenuOpen || suggestions.length === 0) return;
    
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeMenu();
        break;
        
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((selectedIndex + 1) % suggestions.length);
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(
          selectedIndex === 0 ? suggestions.length - 1 : selectedIndex - 1
        );
        break;
        
      case 'Enter':
      case 'Tab':
        // Eğer menu açıksa ve suggestion seçiliyse
        if (isMenuOpen && suggestions[selectedIndex]) {
          e.preventDefault();
          handleSelectedCommandExecute(suggestions);
        }
        break;
    }
  }, [isMenuOpen, selectedIndex, setSelectedIndex, closeMenu, handleSelectedCommandExecute]);
  
  /**
   * Close Menu Handler
   * 
   * Menu'yü kapat
   */
  const handleCloseMenu = useCallback(() => {
    closeMenu();
  }, [closeMenu]);
  
  return {
    // State
    isMenuOpen,
    selectedIndex,
    
    // Actions
    handleInputChange,
    handleCommandSelect,
    handleKeyDown,
    handleCloseMenu,
    handleSelectedCommandExecute,
  };
};

